<template>
  <v-container fluid fill-height>
    <v-row justify="center">
      <v-col cols="12">
        <v-row justify="center">
          <v-col cols="12" md="6" lg="4">
            <NotificationStats
              :acceptedCount="acceptedNotifications.length"
              :pendingCount="pendingNotifications.length"
              :urgentCount="urgentNotifications.length"
              :expiredCount="expiredNotifications.length"
              class="mb-4"
            >
            </NotificationStats>
            <NotificationsList
              data-v-step="301"
              :pending="pendingNotifications.slice(0, 7)"
              :urgent="urgentNotifications.slice(0, 7)"
              :expired="expiredNotifications.slice(0, 7)"
              :accepted="acceptedNotifications.slice(0, 7)"
              class="mt-4 v-step-1"
            >
            </NotificationsList>
          </v-col>

          <v-col cols="12" md="6" lg="4">
            <ManagerStats
              :acceptedCount="assignedAcceptedNotifications.length"
              :urgentCount="assignedUrgentNotifications.length"
              :pendingCount="assignedPendingNotifications.length"
              :expiredCount="assignedExpiredNotifications.length"
              class="mb-4"
            >
            </ManagerStats>

            <!-- mostrar barra de progreso circular/spinner dentro del componente que lo contiene -->
            <template>
              <div class="component-container" style="position: relative">
                <!-- contenido del componente dentro de un div -->
                <div>
                  <ManagerList
                    class="ma-1"
                    :toAccept="assignedNotificationsNotAccepted"
                    :accepted="assignedAcceptedNotifications"
                    :urgent="assignedUrgentNotifications"
                    :pending="assignedPendingNotifications"
                    :expired="assignedExpiredNotifications"
                  >
                  </ManagerList>
                </div>
                <!-- spinner centrado dentro del contenedor -->
                <div v-if="!groupAccessReady" class="spinner-overlay">
                  <v-progress-circular
                    :size="50"
                    color="primary"
                    indeterminate
                  ></v-progress-circular>
                </div>
              </div>
            </template>

            <NotificationsToAcceptDialog
              v-if="
                groupAccessReady && assignedNotificationsNotAccepted.length > 0
              "
              :toAccept="assignedNotificationsNotAccepted.length"
              :toAcceptOperator="toAcceptOperator.length"
              :toAcceptGroup="toAcceptGroup.length"
              :groupName="groupName"
              :shake-enabled="true"
            />

            <TermsToAcceptDialog v-if="!isTermsAccepted" />
            <LicenseRenewedDialog v-if="isLicenseJustRenewed" />
            <CertificateAlertDialog v-if="isCertificateAlert" />

            <!-- Reutilizo el componente de Analytics para cargar las Notificaciones sin NIF titular -->
            <!-- y añado 2 nuevas propiedades al componente, manteniendo la compatibilidad hacia atras -->
            <NotificationsGridModal
              v-if="isSuperAdminUser || isSuperOperatorUser"
              :notifications="notificationsWithoutNifTitular"
              :showDialog="openGridNotifications"
              :title="$t('notificationsWithoutNifTitularDialog.title')"
              :icon="$t('notificationsWithoutNifTitularDialog.icon')"
              :iconColor="$t('notificationsWithoutNifTitularDialog.iconColor')"
              @dialog-closed="closeGrid"
            >
            </NotificationsGridModal>
          </v-col>
          <v-col cols="12" md="6" lg="4">
            <AlertStats
              assetType="notifications"
              :totalCount="withAlertNotifications.length"
              :urgentCount="withAlertUrgentNotifications.length"
              :pendingCount="withAlertPendingNotifications.length"
              :expiredCount="withAlertExpiredNotifications.length"
              class="mb-4"
            >
            </AlertStats>
            <AlertsList
              AlertListElement="AlertListElementNotifications"
              :urgent="withAlertUrgentNotifications"
              :expired="withAlertExpiredNotifications"
              :pending="withAlertPendingNotifications"
              class="mt-4"
            >
            </AlertsList>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import AlertStats from '@/components/dashboard/dashboardElements/common/AlertStats'
import AlertsList from '@/components/dashboard/dashboardElements/common/AlertsList'
import ManagerStats from '@/components/dashboard/dashboardElements/notifications/ManagerStats'
import NotificationsList from '@/components/dashboard/dashboardElements/notifications/NotificationsList'
import NotificationStats from '@/components/dashboard/dashboardElements/notifications/NotificationStats'
import ManagerList from '@/components/dashboard/dashboardElements/notifications/ManagerList'
import {
  getNotificationStatus,
  canOperatorBeManager,
} from '@/services/notification-service'
import { getAlertStatus } from '@/services/alert-service'
import { isGroupRef } from '@/services/user-service'
import { getGroup } from '@/services/groups-service'
import {
  getOperatorsSubscription,
  clearManageOperatorsCache,
} from '@/services/operator-service'
import NotificationsToAcceptDialog from '@/components/dashboard/dashboardElements/notifications/NotificationsToAcceptDialog'
import TermsToAcceptDialog from '@/components/commons/TermsToAcceptDialog'
import LicenseRenewedDialog from '@/components/commons/LicenseRenewedDialog'
import CertificateAlertDialog from '@/components/commons/CertificateAlertDialog'
import NotificationsGridModal from '@/components/analytics/graphs/NotificationsGridModal'

import { mapGetters } from 'vuex'

export default {
  components: {
    AlertsList,
    AlertStats,
    ManagerStats,
    NotificationsList,
    NotificationStats,
    ManagerList,
    NotificationsToAcceptDialog,
    TermsToAcceptDialog,
    LicenseRenewedDialog,
    CertificateAlertDialog,
    NotificationsGridModal,
  },
  data() {
    return {
      openGridNotifications: false,
      groupAccessMapToAccept: {}, // Asignado a grupo y Pendiente de aceptar por el usuario, cada notificationId + boolean
      groupAccessMapAccepted: {}, // Asignado a grupo y ya Aceptada por el usuario, cada notificationId + boolean
      groupAccessReady: false,
      groupAccessLoading: false,
      showLoadingDialog: true, // control del v-dialog del spinner
      groupName: '',
    }
  },
  async beforeMount() {
    const userId = this.isOperatorUser
      ? this.$store.state.user.parentRef.id
      : this.$store.state.user.id

    if (!this.$store.state.manageOperators?.length) {
      this.operatorsUnsubscribe = await getOperatorsSubscription(userId)
    }
  },
  async destroyed() {
    if (this.$store.state.manageOperators) {
      // Limpiamos en el store
      clearManageOperatorsCache()
    }
    if (this.operatorsUnsubscribe) this.operatorsUnsubscribe()
  },
  async mounted() {
    this.preloadGroupAccess()
  },
  computed: {
    ...mapGetters([
      'isTermsAccepted',
      'isLicenseJustRenewed',
      'isCertificateAlert',
      'isAdminUser',
      'isSuperAdminUser',
      'isSuperOperatorUser',
      'isOperatorUser',
    ]),
    notifications() {
      return this.$store.state.notifications
    },
    //----------------Notifications-------------------
    pendingNotifications() {
      return this.notifications.filter((notification) => {
        return (
          getNotificationStatus(notification).status ==
            'notificationStatus.pending' ||
          getNotificationStatus(notification).status ==
            'notificationStatus.appeared'
        )
      })
    },
    acceptedNotifications() {
      return this.notifications.filter((notification) => {
        return (
          getNotificationStatus(notification).status ==
            'notificationStatus.accepted' ||
          getNotificationStatus(notification).status ==
            'notificationStatus.processing' ||
          getNotificationStatus(notification).status ==
            'notificationStatus.completed'
        )
      })
    },
    urgentNotifications() {
      return this.notifications.filter((notification) => {
        return (
          getNotificationStatus(notification).status ==
          'notificationStatus.urgent'
        )
      })
    },
    expiredNotifications() {
      return this.notifications.filter((notification) => {
        return (
          getNotificationStatus(notification).status ==
          'notificationStatus.expired'
        )
      })
    },
    notificationsWithoutNifTitular() {
      return this.notifications.filter((notification) => {
        // por si acaso viniera un nif con uno, o mas de uno, espacios en blanco
        //return !notification.nif_titular?.trim()
        return this.isEmptyNifTitular(notification)
      })
    },
    //----------------Alerts-------------------
    withAlertNotifications() {
      return this.notifications
        .filter((notification) => {
          return notification.alert
        })
        .sort(this.compareAlertDate)
    },
    withAlertUrgentNotifications() {
      return this.withAlertNotifications.filter((notification) => {
        return getAlertStatus(notification.alert).status == 'alertStatus.urgent'
      })
    },
    withAlertPendingNotifications() {
      return this.withAlertNotifications.filter((notification) => {
        return (
          getAlertStatus(notification.alert).status == 'alertStatus.pending'
        )
      })
    },
    withAlertExpiredNotifications() {
      return this.withAlertNotifications.filter((notification) => {
        return (
          getAlertStatus(notification.alert).status == 'alertStatus.expired'
        )
      })
    },

    //------Manager-------
    assignedNotifications() {
      return this.notifications
        .filter((notification) => {
          if (notification.manager) {
            return (
              notification.manager.id == this.$store.state.user.id ||
              // asignada a grupo (resultado calculado previamente)
              this.groupAccessMapAccepted[notification.id] === true
            )
          }
        })
        .sort(this.compareDate)
    },
    assignedAcceptedNotifications() {
      return this.assignedNotifications.filter((notification) => {
        return (
          getNotificationStatus(notification).status ==
            'notificationStatus.accepted' ||
          getNotificationStatus(notification).status ==
            'notificationStatus.processing' ||
          getNotificationStatus(notification).status ==
            'notificationStatus.completed'
        )
      })
    },
    assignedNotificationsNotAccepted() {
      return this.notifications
        .filter((notification) => {
          // asignada al usuario
          if (
            notification.managerAccepted === false &&
            notification.managerRef?.id == this.$store.state.user.id
          ) {
            return true
          }
          // asignada a grupo (resultado calculado previamente)
          return (
            notification.managerAccepted === false &&
            this.groupAccessMapToAccept[notification.id] === true
          )
        })
        .sort(this.compareDate)
    },
    toAcceptOperator() {
      return this.notifications.filter((notification) => {
        // asignada al usuario
        if (
          notification.managerAccepted === false &&
          notification.managerRef?.id == this.$store.state.user.id
        ) {
          return true
        }
      })
    },
    toAcceptGroup() {
      return this.notifications.filter((notification) => {
        // asignada a grupo (resultado calculado previamente)
        return (
          notification.managerAccepted === false &&
          this.groupAccessMapToAccept[notification.id] === true
        )
      })
    },
    assignedUrgentNotifications() {
      return this.notifications
        .filter((notification) => {
          if (notification.manager) {
            return (
              notification.manager.id == this.$store.state.user.id ||
              // asignada a grupo (resultado calculado previamente)
              this.groupAccessMapAccepted[notification.id] === true
            )
          }
        })
        .filter((notification) => {
          return (
            getNotificationStatus(notification).status ==
            'notificationStatus.urgent'
          )
        })
    },
    assignedPendingNotifications() {
      return this.notifications
        .filter((notification) => {
          if (notification.manager) {
            return (
              notification.manager.id == this.$store.state.user.id ||
              // asignada a grupo (resultado calculado previamente)
              this.groupAccessMapAccepted[notification.id] === true
            )
          }
        })
        .filter((notification) => {
          return (
            getNotificationStatus(notification).status ==
              'notificationStatus.pending' ||
            getNotificationStatus(notification).status ==
              'notificationStatus.appeared'
          )
        })
    },
    assignedExpiredNotifications() {
      return this.notifications
        .filter((notification) => {
          if (notification.manager) {
            return (
              notification.manager.id == this.$store.state.user.id ||
              // asignada a grupo (resultado calculado previamente)
              this.groupAccessMapAccepted[notification.id] === true
            )
          }
        })
        .filter((notification) => {
          return (
            getNotificationStatus(notification).status ==
            'notificationStatus.expired'
          )
        })
    },
  },
  methods: {
    compareNotificationDate(a, b) {
      if (
        new Date(a.fecha_puesta_disposicion) <
        new Date(b.fecha_puesta_disposicion)
      ) {
        return -1
      }
      if (
        new Date(a.fecha_puesta_disposicion) >
        new Date(b.fecha_puesta_disposicion)
      ) {
        return 1
      }
      return 0
    },
    compareAlertDate(a, b) {
      if (new Date(a.alert.date) < new Date(b.alert.date)) {
        return -1
      }
      if (new Date(a.alert.date) > new Date(b.alert.date)) {
        return 1
      }
      return 0
    },
    closeGrid() {
      this.openGridNotifications = false
    },
    isEmptyNifTitular(item) {
      return !(item.nif_titular && item.nif_titular.trim().length > 0)
    },
    isGroup(ref) {
      return isGroupRef(ref)
    },
    hasOperatorResponded(notification, operator) {
      if (!operator) return false
      return notification?.responseOperators?.some(
        (r) => r.userRef.id === operator.id
      )
    },
    async preloadGroupAccess() {
      this.groupAccessLoading = true
      this.groupAccessReady = false

      const user = this.$store.state.user
      const tempMapToAccept = {}
      const tempMapAccepted = {}

      let userGroupRef = null
      if (user.groupsRef?.length) {
        userGroupRef = user.groupsRef[0]
        const group = userGroupRef ? await getGroup(userGroupRef) : ''
        this.groupName = group?.name ?? ''
      }
      for (const notification of this.notifications) {
        if (!this.isGroup(notification.managerRef)) continue

        const belongs = userGroupRef?.id === notification.managerRef.id

        if (!belongs) {
          tempMapToAccept[notification.id] = false
          tempMapAccepted[notification.id] = false
          continue
        }

        const canAccess = canOperatorBeManager(user, notification)

        // si el operador ha respondido algo entonces ya no esta pendiente
        const hasResponded = notification.responseOperators?.some(
          (r) => r.userRef.id === user.id
        )

        tempMapToAccept[notification.id] =
          canAccess && !hasResponded && !user.disabled

        // si el operador ha respondido que sí acepta la responsabilidad es como si se la hubieran
        // asignado a él directamente y fuese el managerRef
        const hasRespondedAccepted = notification.responseOperators?.some(
          (r) => r.userRef.id === user.id && r.response === true
        )

        tempMapAccepted[notification.id] =
          canAccess && hasRespondedAccepted && !user.disabled
      }

      // una vez resueltos todos los calculos y los await volcamos todo el mapa y solo entonces se
      // el dialogo modal al usuario y los datos finales sin parpadeos
      this.groupAccessMapToAccept = tempMapToAccept
      this.groupAccessMapAccepted = tempMapAccepted
      this.groupAccessReady = true
      this.groupAccessLoading = false
    },
  },
  watch: {
    notificationsWithoutNifTitular: {
      handler(newArray) {
        this.openGridNotifications = newArray.length > 0
      },
      immediate: true, // Ejecuta el watcher cuando el componente se monta
    },
    notifications: {
      handler(newList, oldList) {
        if (!oldList || newList === oldList) return
        this.preloadGroupAccess()
      },
    },
    '$store.state.manageOperators': {
      async handler(newVal, oldVal) {
        if (!oldVal || newVal === oldVal) return

        // los operadores suscritos han cambiado, recalcula accesos
        await this.preloadGroupAccess()
      },
    },
    groupAccessReady(val) {
      this.showLoadingDialog = !val
    },
  },
}
</script>

<style lang="scss" scoped>
.spinner-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(255, 255, 255, 0.5); /* Fondo semi-transparente */
  border-radius: 8px;
  padding: 20px;
  z-index: 10; /* Por encima del contenido */
  display: flex;
  align-items: center;
  justify-content: center;
}
.component-container {
  position: relative; /* necesario para que el overlay se posicione respecto a este contenedor */
}
</style>
